<div class="pricing-page">

  <!-- ══════════ HERO — calm, full viewport, nothing competing ══════════ -->
  <header class="pricing-hero">
    <div class="hero-bg-glow"></div>
    <div class="wrap hero-inner">
      <h1 class="hero-title">
        Vibe code your App<br class="title-break" />
        with a <span class="text-accent">product team</span> in your corner.
      </h1>
      <p class="hero-lead">
        Five AI agents build and deploy your Angular app. When they hit a wall, a real Product Builder takes over.
      </p>
    </div>
    <div class="scroll-cue" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
        <path d="M3 6L8 11L13 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
  </header>

  <!-- ══════════ PROOF + CONTROLS — first thing revealed on scroll ══════════ -->
  <section class="wrap reveal proof-controls-block">
    <div class="platform-pills">
      <span class="pill">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
          <path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Live Vercel URL every time
      </span>
      <span class="pill">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
          <path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Instant rollback to any deploy
      </span>
      <span class="pill pill-highlight">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="7" r="3" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 1v1M8 13v1M1 7h1M13 7h1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        Product team on every paid project
      </span>
    </div>

    <div class="controls-inner">
      <div class="plan-type-toggle">
        <button class="toggle-btn" [class.active]="!isEnterprise()" (click)="setPlanType('individual')">
          Individual
        </button>
        <button class="toggle-btn" [class.active]="isEnterprise()" (click)="setPlanType('enterprise')">
          Enterprise
        </button>
      </div>

      <div class="billing-toggle-wrap" *ngIf="!isEnterprise()">
        <span class="billing-label" [class.active-label]="!isYearly()">Monthly</span>
        <button class="billing-switch" [class.yearly]="isYearly()"
          (click)="setBilling(isYearly() ? 'monthly' : 'yearly')" aria-label="Toggle billing">
          <span class="switch-thumb"></span>
        </button>
        <span class="billing-label" [class.active-label]="isYearly()">
          Yearly <span class="save-pill">Save ~15%</span>
        </span>
      </div>
    </div>
  </section>

  <!-- ══════════ MAIN ══════════ -->
  <main class="wrap pricing-main">

    <!-- INDIVIDUAL -->
    <ng-container *ngIf="!isEnterprise()">
      <div class="cards-grid">
        <div
          class="pricing-card reveal"
          *ngFor="let plan of activePlans; let i = index"
          [style.transition-delay.ms]="i * 80"
          [class.featured]="plan.isFeatured"
          [class.free-card]="plan.id === 'free'">

          <div class="card-badge" *ngIf="plan.badge">{{ plan.badge }}</div>

          <!-- Header -->
          <div class="card-header">
            <div class="plan-icon">{{ plan.icon }}</div>
            <div>
              <h2 class="plan-name">{{ plan.name }}</h2>
              <p class="plan-tagline">{{ plan.tagline }}</p>
            </div>
          </div>

          <!-- Price -->
          <div class="price-block">
            <div class="price-row">
              <span class="price-amount">{{ getPrice(plan) }}</span>
              <span class="price-period" *ngIf="plan.monthlyPrice! > 0">/mo</span>
              <span class="price-period" *ngIf="plan.monthlyPrice === 0">&nbsp;free</span>
            </div>
            <div class="price-meta">
              <span class="savings-pill" *ngIf="isYearly() && getSavings(plan)">
                Save ₹{{ getSavings(plan)!.toLocaleString('en-IN') }}/yr
              </span>
              <span class="billed-note" *ngIf="isYearly() && plan.monthlyPrice! > 0">
                billed ₹{{ (plan.yearlyPrice! * 12).toLocaleString('en-IN') }}/yr
              </span>
              <span class="lifetime-tag" *ngIf="plan.id === 'free'">
                One-time, never expires
              </span>
            </div>

            <!-- Credits badge -->
            <div class="credits-badge" [class.credits-badge-free]="plan.id === 'free'">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L10 6H14L11 9L12 13L8 11L4 13L5 9L2 6H6L8 2Z"
                  stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
              </svg>
              <div class="credits-text">
                <strong>{{ plan.creditsLabel }}</strong>
                <span>{{ plan.creditsSubtext }}</span>
              </div>
            </div>
          </div>

          <div class="divider"></div>

          <!-- Features: only the headline differentiators show by default -->
          <ul class="features-list">
            <ng-container *ngFor="let feature of plan.features">
              <li class="feature-item"
                *ngIf="!feature.isCheckbox && (!feature.hidden || isExpanded(plan.id))">
                <span class="check-icon">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="2.2"
                      stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="feature-text">{{ feature.label }}</span>
              </li>
            </ng-container>
          </ul>

          <button
            class="show-more-btn"
            type="button"
            *ngIf="hiddenFeatureCount(plan) > 0"
            (click)="toggleExpand(plan.id)">
            {{ isExpanded(plan.id) ? 'Show less' : 'Show ' + hiddenFeatureCount(plan) + ' more' }}
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" [class.rotated]="isExpanded(plan.id)">
              <path d="M3 6L8 11L13 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <!-- Pinned bottom: checkbox (paid only) + CTA -->
          <div class="card-bottom">

            <!-- No developer service notice for free plan -->
            <div class="no-dev-notice" *ngIf="plan.id === 'free'">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/>
                <path d="M8 5v3M8 10.5v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
              </svg>
              Product Builder, chat &amp; board access not on Free
            </div>

            <!-- Checkbox for paid plans only -->
            <ng-container *ngFor="let feature of plan.features">
              <label class="angular-services-checkbox" *ngIf="feature.isCheckbox">
                <input type="checkbox" [checked]="angularServicesOpted()"
                  (change)="angularServicesOpted.set(!angularServicesOpted())"/>
                <span class="custom-checkbox">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="checkbox-label">{{ feature.checkboxLabel }}</span>
              </label>
            </ng-container>

            <button class="cta-btn"
              [class.cta-primary]="plan.ctaStyle === 'primary'"
              [class.cta-outline]="plan.ctaStyle === 'outline'"
              [class.cta-ghost]="plan.ctaStyle === 'ghost'"
              (click)="navigateToCheckout(plan)">
              {{ plan.ctaLabel }}
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" class="cta-arrow">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.6"
                  stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      <!-- Top-up note -->
      <div class="topup-note reveal">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/>
          <path d="M8 5v4M8 11v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
        <span class="topup-text">
          Need more credits? Top up without changing your plan:
          <strong>₹299 for 100 credits</strong> (Starter),
          <strong>₹599 for 250 credits</strong> (Builder / Pro).
          Top-up credits never expire.
        </span>
      </div>
    </ng-container>

    <!-- ENTERPRISE -->
    <ng-container *ngIf="isEnterprise()">
      <div class="enterprise-single-wrap">
        <div class="enterprise-single-card reveal">

          <div class="ent-left">
            <span class="sub-label">Enterprise</span>
            <h2 class="ent-title">A full Angular squad,<br/>built around your org.</h2>
            <div class="ent-price">
              <span class="price-amount">Custom</span>
            </div>
            <p class="ent-subtext">
              Not just a tool. A dedicated product team built around your org,
              with custom credit pools, your own deployment pipeline, SSO, and
              people who understand your codebase and respond when it matters.
            </p>
            <div class="ent-actions">
              <a routerLink="/contact-us" class="cta-btn cta-primary ent-cta">
                Book a Demo
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" class="cta-arrow">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.6"
                    stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
              <p class="ent-reply-note">Usually replies within 2 business hours</p>
            </div>
          </div>

          <div class="ent-right">
            <p class="ent-features-heading">What's included</p>
            <ul class="ent-features-list">
              <ng-container *ngFor="let feature of enterprisePlan.features">
                <li *ngIf="!feature.isCheckbox && (!feature.hidden || isExpanded('enterprise'))">
                  <span class="check-icon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="2.2"
                        stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                  <span>{{ feature.label }}</span>
                </li>
              </ng-container>
            </ul>

            <button
              class="show-more-btn"
              type="button"
              *ngIf="hiddenFeatureCount(enterprisePlan) > 0"
              (click)="toggleExpand('enterprise')">
              {{ isExpanded('enterprise') ? 'Show less' : 'Show ' + hiddenFeatureCount(enterprisePlan) + ' more' }}
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" [class.rotated]="isExpanded('enterprise')">
                <path d="M3 6L8 11L13 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <ng-container *ngFor="let feature of enterprisePlan.features">
              <label class="angular-services-checkbox ent-checkbox-full" *ngIf="feature.isCheckbox">
                <input type="checkbox" [checked]="angularServicesOpted()"
                  (change)="angularServicesOpted.set(!angularServicesOpted())"/>
                <span class="custom-checkbox">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="checkbox-label">{{ feature.checkboxLabel }}</span>
              </label>
            </ng-container>
          </div>

        </div>
      </div>
    </ng-container>

  </main>

  <!-- ══════════ PRODUCT BUILDER SERVICES — the team, sold plainly ══════════ -->
  <section class="wrap dev-explainer-section reveal" *ngIf="!isEnterprise()">
    <div class="dev-explainer">
      <div class="dev-explainer-header">
        <span class="sub-label">Product Builder Services</span>
        <h2 class="dev-explainer-title">A full product team, without the full-time hire.</h2>
        <p class="dev-explainer-sub">
          Developers, designers, and QA sit behind every paid project, no résumés, no salaries, no runway to protect. Everything happens inside your project: requests, chat, and status, no Asana, no Jira, no separate Slack channel.
        </p>
        <div class="capability-pills">
          <span class="pill">API integrations</span>
          <span class="pill">Custom domains &amp; DNS</span>
          <span class="pill">Database &amp; auth setup</span>
          <span class="pill">Third-party integrations</span>
          <span class="pill">Bug fixes &amp; QA</span>
          <span class="pill">Team chat &amp; boards built in</span>
        </div>
      </div>
      <ol class="dev-steps">
        <li class="dev-step">
          <span class="step-num">01</span>
          <div>
            <h4>Raise a request</h4>
            <p>Describe it inside your project. No ticket, no email, no separate Asana or Jira board to keep updated.</p>
          </div>
        </li>
        <li class="dev-step">
          <span class="step-num">02</span>
          <div>
            <h4>The right person picks it up</h4>
            <p>Developer, designer, or QA, whoever fits the request reviews your actual generated code.</p>
          </div>
        </li>
        <li class="dev-step">
          <span class="step-num">03</span>
          <div>
            <h4>Honest about scope</h4>
            <p>A quick fix or a full build like auth or API integration, you get a clear quote before work starts.</p>
          </div>
        </li>
        <li class="dev-step">
          <span class="step-num">04</span>
          <div>
            <h4>Pay for what shipped</h4>
            <p>No retainer, no bench you're funding. Just the request you raised, done.</p>
          </div>
        </li>
      </ol>
    </div>
  </section>

  <!-- ══════════ FAQ ══════════ -->
  <section class="wrap faq-section reveal">
    <div class="faq-grid">
      <div class="faq-item">
        <h4>Does Free include a Product Builder?</h4>
        <p>No. That's on paid plans only. Free gives you 125 lifetime credits to try the platform.</p>
      </div>
      <div class="faq-item">
        <h4>What can my Product Builder do?</h4>
        <p>Fix bugs, ship features, and weigh in on product decisions. If something is out of scope, they'll tell you honestly.</p>
      </div>
      <div class="faq-item">
        <h4>Can I use my own domain?</h4>
        <p>Builder and Pro include custom domains with SSL. Free and Starter publish to an <code>angularize.dev</code> subdomain.</p>
      </div>
      <div class="faq-item">
        <h4>What if I run out of credits?</h4>
        <p>Top up anytime: ₹299 for 100 credits (Starter) or ₹599 for 250 (Builder/Pro). Top-ups never expire.</p>
      </div>
    </div>
  </section>

</div>